export function rectangleDe(placement, oeuvre) {
    if (placement.cadre)
        return {
            x: placement.xCm,
            y: placement.yCm,
            largeur: placement.cadre.largeurCm,
            hauteur: placement.cadre.hauteurCm,
            provisoire: false,
        };
    if (oeuvre &&
        Number.isFinite(oeuvre.largeur) &&
        Number.isFinite(oeuvre.hauteur) &&
        oeuvre.largeur > 0 &&
        oeuvre.hauteur > 0) {
        return {
            x: placement.xCm,
            y: placement.yCm,
            largeur: oeuvre.largeur,
            hauteur: oeuvre.hauteur,
            provisoire: false,
        };
    }
    const taille = placement.tailleProvisoire ?? { largeurCm: 50, hauteurCm: 50 };
    return {
        x: placement.xCm,
        y: placement.yCm,
        largeur: taille.largeurCm,
        hauteur: taille.hauteurCm,
        provisoire: true,
    };
}
export function placerSurAxe(_largeur, hauteur, axeCm) {
    return axeCm - hauteur / 2;
}
export function aimanter(rect, contexte, seuilCm = 2) {
    const candidats = [];
    function ajouter(direction, position, nature, positionCm, oeuvreIds = [], ecartCm) {
        candidats.push({
            position,
            repere: {
                nature,
                direction,
                positionCm,
                oeuvreIds,
                ...(ecartCm === undefined ? {} : { ecartCm }),
            },
        });
    }
    ajouter('y', placerSurAxe(rect.largeur, rect.hauteur, contexte.axeCm), 'axe', contexte.axeCm);
    for (const direction of ['x', 'y']) {
        const taille = direction === 'x' ? rect.largeur : rect.hauteur;
        const limite = direction === 'x' ? contexte.mur.largeurCm : contexte.mur.hauteurCm;
        ajouter(direction, 0, 'bord_mur', 0);
        ajouter(direction, limite - taille, 'bord_mur', limite);
        for (const voisin of contexte.voisins) {
            const debut = voisin[direction];
            const fin = debut + (direction === 'x' ? voisin.largeur : voisin.hauteur);
            for (const bord of [debut, fin]) {
                ajouter(direction, bord, 'bord_oeuvre', bord, [voisin.oeuvreId]);
                ajouter(direction, bord - taille, 'bord_oeuvre', bord, [voisin.oeuvreId]);
            }
        }
        // Espacements égaux : l'écart du premier couple répété avant la première
        // œuvre, celui du dernier couple répété après la dernière.
        const voisins = contexte.voisins.toSorted((a, b) => a[direction] - b[direction]);
        const fin = (voisin) => voisin[direction] + (direction === 'x' ? voisin.largeur : voisin.hauteur);
        if (voisins.length >= 2) {
            const premier = voisins[0];
            const second = voisins[1];
            const ecartDebut = second[direction] - fin(premier);
            if (ecartDebut >= 0) {
                ajouter(direction, premier[direction] - ecartDebut - taille, 'espacement_egal', premier[direction] - ecartDebut, [premier.oeuvreId, second.oeuvreId], ecartDebut);
            }
            const avantDernier = voisins[voisins.length - 2];
            const dernier = voisins[voisins.length - 1];
            const ecartFin = dernier[direction] - fin(avantDernier);
            if (ecartFin >= 0) {
                ajouter(direction, fin(dernier) + ecartFin, 'espacement_egal', fin(dernier) + ecartFin, [avantDernier.oeuvreId, dernier.oeuvreId], ecartFin);
            }
        }
    }
    const rectangle = { ...rect };
    const reperes = [];
    for (const direction of ['x', 'y']) {
        const proches = candidats.filter((candidat) => candidat.repere.direction === direction &&
            Math.abs(candidat.position - rect[direction]) <= seuilCm);
        proches.sort((a, b) => Math.abs(a.position - rect[direction]) - Math.abs(b.position - rect[direction]));
        const choisi = proches[0];
        if (!choisi)
            continue;
        rectangle[direction] = choisi.position;
        reperes.push(...proches
            .filter((candidat) => candidat.position === choisi.position)
            .map((candidat) => candidat.repere));
    }
    return { rectangle, reperes };
}
function seChevauchent(a, b) {
    // Un contact en cm décimaux ne doit pas devenir un chevauchement par erreur de flottant.
    return (b.x + b.largeur - a.x > 1e-9 &&
        a.x + a.largeur - b.x > 1e-9 &&
        b.y + b.hauteur - a.y > 1e-9 &&
        a.y + a.hauteur - b.y > 1e-9);
}
export function alertesDuMur(mur, rects) {
    const alertes = [];
    rects.forEach((rect, indice) => {
        if (rect.provisoire)
            alertes.push({ nature: 'sans_dimensions', oeuvreId: rect.oeuvreId });
        if (rect.x < 0 ||
            rect.y < 0 ||
            rect.x + rect.largeur - mur.largeurCm > 1e-9 ||
            rect.y + rect.hauteur - mur.hauteurCm > 1e-9) {
            alertes.push({ nature: 'debordement', oeuvreId: rect.oeuvreId });
        }
        for (const zone of mur.zones) {
            if (seChevauchent(rect, {
                x: zone.xCm,
                y: zone.yCm,
                largeur: zone.largeurCm,
                hauteur: zone.hauteurCm,
                provisoire: false,
            })) {
                alertes.push({ nature: 'zone_interdite', oeuvreId: rect.oeuvreId, zone });
            }
        }
        for (const autre of rects.slice(indice + 1)) {
            if (rect.oeuvreId === autre.oeuvreId)
                throw new Error('placement.oeuvre_en_double');
            if (seChevauchent(rect, autre))
                alertes.push({ nature: 'chevauchement', oeuvreIds: [rect.oeuvreId, autre.oeuvreId] });
        }
    });
    return alertes;
}
export function cotesDuMur(mur, rects) {
    const triees = rects.toSorted((a, b) => a.x - b.x || b.y - a.y);
    return {
        oeuvres: triees.map((rect) => ({
            oeuvreId: rect.oeuvreId,
            gaucheCm: rect.x,
            droiteCm: mur.largeurCm - rect.x - rect.largeur,
            hautCm: mur.hauteurCm - rect.y - rect.hauteur,
            basCm: rect.y,
            centreCm: rect.y + rect.hauteur / 2,
        })),
        espacements: triees.slice(1).map((rect, indice) => ({
            oeuvreIds: [triees[indice].oeuvreId, rect.oeuvreId],
            distanceCm: rect.x - triees[indice].x - triees[indice].largeur,
        })),
    };
}
/**
 * Le point d'accroche : au milieu de l'œuvre, sous son bord haut du décalage
 * saisi. Pour une œuvre centrée sur l'axe, `y + h = axe + h / 2`.
 */
export function crochet(rect, decalageCm) {
    return {
        distanceBordGaucheCm: rect.x + rect.largeur / 2,
        hauteurCm: rect.y + rect.hauteur - decalageCm,
    };
}
/** Ordre du parcours : ordre des murs, puis de gauche à droite, puis de haut en bas. */
export function ordreDuParcours(plan, espace) {
    const murs = new Map(espace.murs.map((mur) => [mur._id, mur.ordre]));
    const oeuvres = new Set();
    for (const placement of plan.placements) {
        if (oeuvres.has(placement.oeuvreId))
            throw new Error('placement.oeuvre_en_double');
        oeuvres.add(placement.oeuvreId);
    }
    return plan.placements
        .toSorted((a, b) => (murs.get(a.murId) ?? Infinity) - (murs.get(b.murId) ?? Infinity) ||
        a.xCm - b.xCm ||
        b.yCm - a.yCm)
        .map((placement) => placement.oeuvreId);
}
export function deplacer(rect, direction, grand = false) {
    const pas = grand ? 10 : 1;
    const rectangle = { ...rect };
    if (direction === 'gauche')
        rectangle.x -= pas;
    if (direction === 'droite')
        rectangle.x += pas;
    if (direction === 'haut')
        rectangle.y += pas;
    if (direction === 'bas')
        rectangle.y -= pas;
    return rectangle;
}
/** La plus grande échelle (dénominateur) qui fait tenir le mur, `null` sinon. */
export function echelleDeLaFeuille(largeurMurCm, hauteurMurCm, zoneImprimableMm) {
    return ([10, 20, 25, 50, 100, 200].find((echelle) => (largeurMurCm * 10) / echelle <= zoneImprimableMm.largeur &&
        (hauteurMurCm * 10) / echelle <= zoneImprimableMm.hauteur) ?? null);
}
//# sourceMappingURL=moteur.js.map